import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Chip, FormControlLabel, Link, Switch, TextField } from "@mui/material";
import { useGetAssignedOrdersQuery } from "app/api/orders";
import OutlinedCard from "components/OutlinedCard";
import PageLayout from "components/PageLayout";
import PageTitle from "components/PageTitle";
import ErrorPage from "pages/error/ErrorPage";
import LoadingPage from "pages/loading/LoadingPage";
import DataTable, { ColumnOptions } from "components/DataTable";
import { MinimalOrder } from "models/MinimalOrder";

const columns: ColumnOptions<MinimalOrder> = [
  {
    key: "ordernr",
    label: "Ordernr",
    dense: true,
    renderCell: (row) => (
      <Link component={RouterLink} to={`/orders/view/${row.ordernr}`}>
        {row.ordernr}
      </Link>
    ),
  },
  {
    key: "companyName",
    label: "Kund",
  },
  {
    key: "complete",
    label: "Status",
    dense: true,
    align: "right",
    defaultSortDirection: "desc",
    renderCell: (row) => (
      <Chip
        size="small"
        color={row.complete ? "success" : "warning"}
        label={row.complete ? "Slutförd" : "Pågående"}
      />
    ),
  },
];

const AssignedOrdersPage: React.FC = () => {
  const [orderSearch, setOrderSearch] = useState("");
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const {
    data: assignedOrders,
    isLoading,
    error,
  } = useGetAssignedOrdersQuery({ completed: includeCompleted });

  const filteredOrders = useMemo(() => {
    if (!assignedOrders) return [];

    return assignedOrders.filter(
      (order) =>
        order.ordernr.toLowerCase().includes(orderSearch.toLowerCase()) ||
        order.companyName.toLowerCase().includes(orderSearch.toLowerCase())
    );
  }, [assignedOrders, orderSearch]);

  if (isLoading) return <LoadingPage />;

  if (error) return <ErrorPage />;

  return (
    <PageLayout maxWidth="lg">
      <PageTitle>Mina ordrar</PageTitle>

      <OutlinedCard>
        <Box display="flex" justifyContent="start" alignItems="center" gap={1} pb={1}>
          <TextField
            label="Sök order..."
            variant="filled"
            value={orderSearch}
            onChange={(e) => setOrderSearch(e.target.value)}
            sx={{ width: 220, flexGrow: 0 }}
          />

          <FormControlLabel
            label="Visa slutförda"
            sx={{ ml: 1 }}
            control={
              <Switch
                edge="start"
                checked={includeCompleted}
                onChange={() => setIncludeCompleted(!includeCompleted)}
              />
            }
          />
        </Box>

        <DataTable
          columns={columns}
          data={filteredOrders}
          defaultSortBy="complete"
          getKey={(row) => row.ordernr}
          striped
          noBottomBorder
        />
      </OutlinedCard>
    </PageLayout>
  );
};

export default AssignedOrdersPage;
